import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Tabs,
    Tab,
    Spinner,
    DateRangePicker,
    Autocomplete,
    AutocompleteItem,
} from "@heroui/react";
import { LuPrinter } from "react-icons/lu";
import { parseDate } from "@internationalized/date";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import {
    formatCurrency,
    formatDate,
    formatPrice,
    formatThousands,
} from "@/utils";

const TableReport = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTab = searchParams.get("tab") || "today";
    const todayStr = new Date().toISOString().split("T")[0];
    const customRange = {
        start: parseDate(searchParams.get("start") || todayStr),
        end: parseDate(searchParams.get("end") || todayStr),
    };
    const filters = {
        customer_id: searchParams.get("customer_id") || "",
        material_id: searchParams.get("material_id") || "",
        sub_category_id: searchParams.get("sub_category_id") || "",
    };

    const updateParams = (updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v) next.set(k, v);
                else next.delete(k);
            });
            return next;
        });
    };

    // Fetch filter options
    const { data: customers } = useFetchData("select/customer");
    const { data: materials } = useFetchData("select/material");
    const { data: subCategories } = useFetchData(
        "select/material-sub-category",
    );

    const formatDateParam = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const getReportParams = () => {
        const today = new Date();
        const base = {
            customer_id: filters.customer_id || undefined,
            material_id: filters.material_id || undefined,
            sub_category_id: filters.sub_category_id || undefined,
        };

        if (selectedTab === "today") {
            return base;
        }

        if (selectedTab === "this_week") {
            const dayOfWeek = today.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const start = new Date(today);
            start.setDate(today.getDate() + mondayOffset);
            return {
                ...base,
                start_date: formatDateParam(start),
                end_date: formatDateParam(today),
            };
        }

        if (selectedTab === "this_month") {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return {
                ...base,
                start_date: formatDateParam(start),
                end_date: formatDateParam(end),
            };
        }

        if (selectedTab === "custom") {
            return {
                ...base,
                start_date: customRange.start.toString(),
                end_date: customRange.end.toString(),
            };
        }

        return base;
    };

    const { data, isLoading, error, refetch } = useFetchData(
        "report/daily-sales",
        getReportParams(),
    );

    const salesData = data?.sales || [];
    const recapData = data?.recap || [];
    const period = data?.period || {};

    // Calculate totals
    const grandTotal = salesData.reduce(
        (sum, item) => sum + Number(item.subtotal || 0),
        0,
    );
    const totalQuantity = salesData.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0,
    );

    const recapGrandTotal = recapData.reduce(
        (sum, item) => sum + Number(item.total_amount || 0),
        0,
    );

    // Period display
    const periodDisplay =
        period.start === period.end
            ? formatDate(period.start)
            : `${formatDate(period.start)} - ${formatDate(period.end)}`;

    if (error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Laporan"
                    description="Terjadi kesalahan saat memuat data laporan penjualan."
                    onAction={refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex items-center justify-between">
                <PageHeader
                    title="Laporan Penjualan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Laporan" },
                        { label: "Penjualan" },
                    ]}
                />
                <Button
                    color="default"
                    size="sm"
                    startContent={<LuPrinter />}
                    isDisabled={isLoading || salesData.length === 0}
                    onPress={() => {
                        const p = getReportParams();
                        const qs = new URLSearchParams();
                        if (p.start_date) qs.set("start_date", p.start_date);
                        if (p.end_date) qs.set("end_date", p.end_date);
                        if (p.customer_id) qs.set("customer_id", p.customer_id);
                        if (p.material_id) qs.set("material_id", p.material_id);
                        if (p.sub_category_id)
                            qs.set("sub_category_id", p.sub_category_id);
                        navigate(`/sales-report/print?${qs.toString()}`);
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Tabs
                        aria-label="Date Filter"
                        selectedKey={selectedTab}
                        onSelectionChange={(key) => updateParams({ tab: key })}
                        variant="solid"
                        size="sm"
                    >
                        <Tab key="today" title="Hari Ini" />
                        <Tab key="this_week" title="Minggu Ini" />
                        <Tab key="this_month" title="Bulan Ini" />
                        <Tab key="custom" title="Custom Range" />
                    </Tabs>

                    {selectedTab === "custom" && (
                        <DateRangePicker
                            aria-label="Pilih Periode"
                            className="max-w-xs"
                            variant="flat"
                            size="sm"
                            value={customRange}
                            onChange={(range) =>
                                updateParams({
                                    start: range.start.toString(),
                                    end: range.end.toString(),
                                })
                            }
                        />
                    )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Autocomplete
                        aria-label="Filter Jenis Barang"
                        placeholder="Semua Jenis"
                        variant="flat"
                        size="sm"
                        className="max-w-50"
                        selectedKey={filters.sub_category_id || null}
                        onSelectionChange={(key) => updateParams({ sub_category_id: key || "" })}
                        isClearable
                        onClear={() => updateParams({ sub_category_id: "" })}
                    >
                        {(subCategories || []).map((item) => (
                            <AutocompleteItem key={item.value}>{item.title}</AutocompleteItem>
                        ))}
                    </Autocomplete>

                    <Autocomplete
                        aria-label="Filter Barang"
                        placeholder="Semua Barang"
                        variant="flat"
                        size="sm"
                        className="max-w-50"
                        selectedKey={filters.material_id || null}
                        onSelectionChange={(key) => updateParams({ material_id: key || "" })}
                        isClearable
                        onClear={() => updateParams({ material_id: "" })}
                    >
                        {(materials || []).map((item) => (
                            <AutocompleteItem key={item.value}>{item.title}</AutocompleteItem>
                        ))}
                    </Autocomplete>

                    <Autocomplete
                        aria-label="Filter Customer"
                        placeholder="Semua Customer"
                        variant="flat"
                        size="sm"
                        className="max-w-50"
                        selectedKey={filters.customer_id || null}
                        onSelectionChange={(key) => updateParams({ customer_id: key || "" })}
                        isClearable
                        onClear={() => updateParams({ customer_id: "" })}
                    >
                        {(customers || []).map((item) => (
                            <AutocompleteItem key={item.value}>{item.title}</AutocompleteItem>
                        ))}
                    </Autocomplete>
                </div>
            </div>

            {/* Laporan Penjualan */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Laporan Penjualan
                    </ThemedText>
                    <ThemedText size="xs" variant="caption">
                        Periode: {periodDisplay}
                    </ThemedText>
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex h-48 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : salesData.length === 0 ? (
                        <EmptyState
                            title="Tidak Ada Data"
                            description="Belum ada data penjualan untuk periode ini."
                        />
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Tanggal</th>
                                    <th>Customer</th>
                                    <th>Barang</th>
                                    <th>Cara Bayar</th>
                                    <th>Jumlah</th>
                                    <th>@</th>
                                    <th>Total Harga</th>
                                    <th>Pembayaran</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesData.map((item, index) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-default-50 transition-colors"
                                    >
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatDate(item.order_date)}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.customer_name}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.material_name}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.payment_method_name}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatThousands(item.quantity)}{" "}
                                                {item.unit_name}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatPrice(item.price)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatCurrency(item.subtotal)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.payment_status_name}
                                            </ThemedText>
                                            {item.latest_payment_date && (
                                                <ThemedText
                                                    size="xxs"
                                                    variant="secondary"
                                                >
                                                    {formatDate(
                                                        item.latest_payment_date,
                                                    )}
                                                </ThemedText>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={5} className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            TOTAL
                                        </ThemedText>
                                    </td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totalQuantity)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-right"></td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(grandTotal)}
                                        </ThemedText>
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Rekap Penjualan */}
            {!isLoading && recapData.length > 0 && (
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                        <ThemedText size="md" className="font-semibold">
                            Rekap Penjualan
                        </ThemedText>
                        <ThemedText size="xs" variant="caption">
                            Periode: {periodDisplay}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="overflow-x-auto p-4 pt-2">
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Kategori Barang</th>
                                    <th>Jumlah</th>
                                    <th>Total Harga</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recapData.map((item, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-default-50 transition-colors"
                                    >
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.sub_category_name ||
                                                    "Tanpa Kategori"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatThousands(
                                                    item.total_quantity,
                                                )}{" "}
                                                {item.unit_name}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatCurrency(
                                                    item.total_amount,
                                                )}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2} className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            TOTAL
                                        </ThemedText>
                                    </td>
                                    <td className="text-right"></td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(recapGrandTotal)}
                                        </ThemedText>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default TableReport;
